import React, { useState, useEffect } from 'react';
import './Dashboard.css';
import './DashboardGrid.css';
import StatCard from './StatCard';
import { dashboardApi, DashboardStats } from '../../services/dashboardApi';

interface DashboardProps {
  onNavigate: (view: string, filter?: 'all' | 'due' | 'missing' | 'overdue' | 'upload' | 'failed') => void;
}

const Dashboard: React.FC<DashboardProps> = ({ onNavigate }) => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [preprocessingStats, setPreprocessingStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadDashboardStats();
  }, []);

  const loadDashboardStats = async () => {
    try {
      setLoading(true);
      const [data, preprocessData] = await Promise.all([
        dashboardApi.getStats(),
        dashboardApi.getPreprocessingStats()
      ]);
      setStats(data);
      setPreprocessingStats(preprocessData);
      setError(null);
    } catch (err) {
      console.error('Failed to load dashboard stats:', err);
      setError('Failed to load dashboard data. Please check your connection and try again.');
      setStats(null);
      setPreprocessingStats(null);
    } finally {
      setLoading(false);
    }
  };

  // Manual refresh — replaces the static "Data refreshes at 2:00 AM" note. We have
  // multiple jobs with different schedules (preprocessor, processors, exporter,
  // picklist, reconciliation), so a single scheduled time was misleading.
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const [data, preprocessData] = await Promise.all([
        dashboardApi.getStats(),
        dashboardApi.getPreprocessingStats()
      ]);
      setStats(data);
      setPreprocessingStats(preprocessData);
      setError(null);
    } catch (err) {
      console.error('Failed to refresh dashboard:', err);
      setError('Failed to refresh data. Please try again.');
    } finally {
      setRefreshing(false);
    }
  };

  const handleNavigate = (section: string) => {
    onNavigate(section);
  };

  // Split stats into sections - 3 tiles for Picklist (removed Pending Processing)
  const picklistStats = stats ? [
    {
      id: 'invoices-due',
      title: 'Due This Week',
      value: stats.dueThisWeek.toString(),
      subtitle: 'Invoices due for collection',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#17a2b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
          <line x1="16" y1="2" x2="16" y2="6"/>
          <line x1="8" y1="2" x2="8" y2="6"/>
          <line x1="3" y1="10" x2="21" y2="10"/>
        </svg>
      ),
      color: 'info' as const,
      onClick: () => onNavigate('harvest', 'due')
    },
    {
      id: 'invoices-overdue',
      title: 'Overdue',
      value: stats.overdue.toString(),
      subtitle: 'Past due invoices',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ffc107" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <polyline points="12 6 12 12 16 14"/>
        </svg>
      ),
      color: 'warning' as const,
      onClick: () => onNavigate('harvest', 'overdue')
    },
    {
      id: 'invoices-missing',
      title: 'Missing',
      value: stats.missing.toString(),
      subtitle: 'Invoices not received',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#dc3545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
          <line x1="12" y1="17" x2="12.01" y2="17"/>
        </svg>
      ),
      color: 'danger' as const,
      onClick: () => onNavigate('harvest', 'missing')
    }
  ] : [];


  // 5 tiles for Queue Statistics
  const queueStats = stats ? [
    {
      id: 'review-queue',
      title: 'Review Queue',
      value: stats.reviewQueue.toString(),
      subtitle: 'Awaiting staff review',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ff9800" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
          <circle cx="12" cy="12" r="3"/>
        </svg>
      ),
      color: 'warning' as const,
      onClick: () => handleNavigate('capture-review')
    },
    {
      id: 'error-queue',
      title: 'Error Queue',
      value: stats.errorQueue.toString(),
      subtitle: 'Processing errors',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#dc3545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
          <line x1="12" y1="9" x2="12" y2="13"/>
          <line x1="12" y1="17" x2="12.01" y2="17"/>
        </svg>
      ),
      color: 'danger' as const,
      onClick: () => handleNavigate('capture-error')
    },
    {
      id: 'duplicates',
      title: 'Duplicates',
      value: stats.duplicates.toString(),
      subtitle: 'Duplicate invoices',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#17a2b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M16 2v5h5"/>
          <path d="M21 11.5V19a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7.5L21 9.5v2z"/>
          <path d="M3 7.5V19a2 2 0 0 0 2 2h12"/>
        </svg>
      ),
      color: 'info' as const,
      onClick: () => handleNavigate('capture-duplicate')
    },
    {
      id: 'unknown',
      title: 'Unknown',
      value: stats.unknown.toString(),
      subtitle: 'Unknown invoices',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6c757d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8"/>
          <path d="m21 21-4.35-4.35"/>
        </svg>
      ),
      color: 'secondary' as const,
      onClick: () => handleNavigate('capture-unknown')
    },
    {
      id: 'processed',
      title: 'Ready for Export',
      value: stats.processedThisWeek.toString(),
      subtitle: 'Awaiting export',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#28a745" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
          <polyline points="22 4 12 14.01 9 11.01"/>
        </svg>
      ),
      color: 'success' as const,
      onClick: () => handleNavigate('capture-success')
    }
  ] : [];

  if (loading) {
    return (
      <div className="dashboard">
        <div className="loading-state">
          <p>Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  // Get next processor schedule run time based on configuration
  return (
    <div className="dashboard">
      {error && (
        <div className="dashboard-error">
          <p className="error-message">{error}</p>
        </div>
      )}
      
      {/* Picklist Statistics Section */}
      <div className="dashboard-section">
        <div className="section-header">
          <h2 className="section-title">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
              <polyline points="10 9 9 9 8 9"/>
            </svg>
            Picklist Statistics
          </h2>
          <button
            type="button"
            className="action-icon-btn dashboard-refresh-btn"
            onClick={handleRefresh}
            disabled={refreshing}
            title="Refresh dashboard data"
            aria-label="Refresh dashboard data"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={refreshing ? 'spinning' : ''}
            >
              <polyline points="23 4 23 10 17 10" />
              <polyline points="1 20 1 14 7 14" />
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
          </button>
        </div>
        <div className="dashboard-grid grid-5">
          {picklistStats.map((stat) => (
            <StatCard
              key={stat.id}
              title={stat.title}
              value={stat.value}
              subtitle={stat.subtitle}
              icon={stat.icon}
              color={stat.color}
              onClick={stat.onClick}
            />
          ))}
          {/* Empty placeholders for consistent 5-column grid */}
          <div className="stat-card-placeholder"></div>
          <div className="stat-card-placeholder"></div>
        </div>
      </div>

      {/* Pre-processing Statistics Section */}
      <div className="dashboard-section">
        <h2 className="section-title">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
            <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
            <line x1="12" y1="22.08" x2="12" y2="12"/>
          </svg>
          Pre-processing Statistics
        </h2>
        <div className="dashboard-grid grid-5">
          <StatCard
            title="Uploaded"
            value={preprocessingStats?.uploaded?.toString() || '0'}
            subtitle="Pending preprocessing"
            icon={
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#17a2b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="17 8 12 3 7 8"/>
                <line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
            }
            color="info"
            onClick={() => onNavigate('harvest', 'upload')}
          />
          <div className="stat-card stat-card-success">
            <div className="stat-card-content">
              <div className="stat-card-left">
                <h3 className="stat-card-title">
                  <span className="stat-card-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#28a745" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="20" x2="18" y2="10"/>
                      <line x1="12" y1="20" x2="12" y2="4"/>
                      <line x1="6" y1="20" x2="6" y2="14"/>
                    </svg>
                  </span>
                  Pre-processed
                </h3>
                <div className="utility-pills">
                  <span className="utility-pill utility-pill-electric">
                    ⚡ {preprocessingStats?.preprocessed?.Electric || 0}
                  </span>
                  <span className="utility-pill utility-pill-gas">
                    🔥 {preprocessingStats?.preprocessed?.Gas || 0}
                  </span>
                  <span className="utility-pill utility-pill-water">
                    💧 {preprocessingStats?.preprocessed?.Water || 0}
                  </span>
                  <span className="utility-pill utility-pill-cws">
                    ❄️ {preprocessingStats?.preprocessed?.CWS || 0}
                  </span>
                </div>
              </div>
              <div className="stat-card-right">
                <div className="stat-card-value">
                  {preprocessingStats?.preprocessed ? 
                    (preprocessingStats.preprocessed.Electric + 
                     preprocessingStats.preprocessed.Gas + 
                     preprocessingStats.preprocessed.Water + 
                     preprocessingStats.preprocessed.CWS) : 0}
                </div>
              </div>
            </div>
          </div>
          <StatCard
            title="Failed"
            value={preprocessingStats?.failed?.toString() || '0'}
            subtitle="Could not be sorted"
            icon={
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#dc3545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="15" y1="9" x2="9" y2="15"/>
                <line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
            }
            color="danger"
            onClick={() => onNavigate('harvest', 'failed')}
          />
          {/* Empty placeholders for consistent 5-column grid */}
          <div className="stat-card-placeholder"></div>
          <div className="stat-card-placeholder"></div>
        </div>
      </div>

      {/* Queue Statistics Section */}
      <div className="dashboard-section">
        <h2 className="section-title">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="8" y1="6" x2="21" y2="6"/>
            <line x1="8" y1="12" x2="21" y2="12"/>
            <line x1="8" y1="18" x2="21" y2="18"/>
            <line x1="3" y1="6" x2="3.01" y2="6"/>
            <line x1="3" y1="12" x2="3.01" y2="12"/>
            <line x1="3" y1="18" x2="3.01" y2="18"/>
          </svg>
          Queue Statistics
        </h2>
        <div className="dashboard-grid grid-5">
          {queueStats.map((stat) => (
            <StatCard
              key={stat.id}
              title={stat.title}
              value={stat.value}
              subtitle={stat.subtitle}
              icon={stat.icon}
              color={stat.color}
              onClick={stat.onClick}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
